/**
 * PosThemeProvider — wraps a POS surface in the Wanderzirkus circus-poster
 * theme (from the Tally POS Design System, project 4faf9d8e).
 *
 * The admin SPA is one app-wide AntD ConfigProvider (cssVar mode) and may be in
 * light OR dark mode. The POS surfaces are dark-first ("big-top at night"), so
 * this nests a ConfigProvider that forces AntD's dark algorithm for the subtree
 * and seeds it with the brand palette — so every AntD component (Button, Input,
 * Segmented, Modal, …) renders correctly branded regardless of how cssVar
 * scopes its variables. The `.pos-surface` wrapper additionally loads the scoped
 * brand tokens (`--bg/-text/-accent/-recipe/…` + the `--ant-color-*` bridge)
 * that the custom `.pos-*` CSS reads. Scoping keeps the rest of the M365 app
 * untouched.
 *
 * The seed hexes below MIRROR tokens/colors.css (dark theme) — keep them in sync
 * if the brand palette is retuned there.
 */
import { ConfigProvider, theme } from "antd";

import "../../styles/pos-theme/index.css";

// Dark-theme brand seeds — mirror of styles/pos-theme/colors.css (.pos-surface).
const POS_THEME = {
  algorithm: theme.darkAlgorithm,
  cssVar: true,
  token: {
    colorPrimary: "#FF5A36", // coral-500
    colorInfo: "#38C7CE", // teal-400
    colorSuccess: "#5DBB5A", // green-500
    colorWarning: "#F5B228", // gold-500
    colorError: "#E5342B", // red-500
    colorTextBase: "#F7EFE2", // paper-100
    colorBgBase: "#0C0A09", // tar-950
    colorBgLayout: "#0C0A09", // bg-app
    colorBgContainer: "#14110E", // bg-surface (tar-900)
    colorBgElevated: "#1A1611", // bg-raised (tar-850)
    colorBorder: "#342D24", // tar-700
    colorSplit: "rgba(247, 239, 226, 0.08)",
    borderRadius: 8, // radius-md
    fontFamily:
      "Archivo, system-ui, -apple-system, 'Segoe UI', sans-serif",
  },
} as const;

export const PosThemeProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => (
  <ConfigProvider theme={POS_THEME}>
    <div className="pos-surface pos-surface-base">{children}</div>
  </ConfigProvider>
);
